import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import "./App.css";
import "./styles/gh.css";
import "./styles/enforcement-print.css";
import { ModeProvider, useMode } from './contexts/ModeContext';
const MSMLKeynote = lazy(() => import('./components/keynote/MSMLKeynote'));
const GHHomePage = lazy(() => import('./components/gh/GHHomePage'));
const DeepDivePage = lazy(() => import('./components/gh/DeepDivePage'));
const StrategicPage = lazy(() => import('./components/gh/StrategicPage'));
const TechnologyPerspective = lazy(() => import('./technology/TechnologyPerspective'));
import AssetsHeader from './components/gh/AssetsHeader';
import Header from './components/Header';
import { setSeo } from './components/gh/seoHead';
import Hero from './components/Hero';
import TechnologySection from './components/TechnologySection';
import ProductsSection from './components/ProductsSection';
import MarketsSection from './components/MarketsSection';
import TargetsSection from './components/TargetsSection';
import RoadmapSection from './components/RoadmapSection';
import AssetsSection from './components/AssetsSection';
import TeamSection from './components/TeamSection';
import InvestmentSection from './components/InvestmentSection';
import ROICalculator from './components/ROICalculator';
import ContactSection from './components/ContactSection';
import Footer from './components/Footer';
import { Toaster } from './components/ui/toaster';

function AppContent() {
  const { mode } = useMode();
  useEffect(() => {
    // Function to remove "Made with Emergent" badge - more targeted
    const removeEmergentBadge = () => {
      // Look for elements containing exactly "Made with Emergent" text
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      const badgeTextNodes = [];
      let node;
      
      while (node = walker.nextNode()) {
        // Only target exact matches to avoid false positives
        if (node.textContent.trim() === 'Made with Emergent' || 
            (node.textContent.includes('Made with Emergent') && node.textContent.length < 30)) {
          badgeTextNodes.push(node);
        }
      }
      
      // Remove parent elements of badge text nodes
      badgeTextNodes.forEach(textNode => {
        let parent = textNode.parentElement;
        while (parent && parent !== document.body) {
          const style = window.getComputedStyle(parent);
          // Only remove if it's clearly a badge (small, positioned, high z-index)
          if ((style.position === 'fixed' || style.position === 'absolute') &&
              parseInt(style.width) < 200 &&
              parseInt(style.height) < 100 &&
              (parseInt(style.zIndex) > 1000 || parent.className.includes('badge') || parent.className.includes('emergent'))) {
            parent.style.display = 'none';
            parent.remove();
            break;
          }
          parent = parent.parentElement;
        }
      });
      
      // Target very specific badge-like elements in bottom corners
      const suspiciousBadges = document.querySelectorAll('[style*="position: fixed"][style*="bottom"][style*="right"]');
      suspiciousBadges.forEach(el => {
        const rect = el.getBoundingClientRect();
        const style = window.getComputedStyle(el);
        // Only remove small elements in the bottom-right corner
        if (rect.width < 200 && rect.height < 60 && 
            rect.right > window.innerWidth - 100 && 
            rect.bottom > window.innerHeight - 100) {
          el.style.display = 'none';
        }
      });
    };

    // Run with delays to avoid interfering with page load
    setTimeout(removeEmergentBadge, 1000);
    setTimeout(removeEmergentBadge, 3000);
    const interval = setInterval(removeEmergentBadge, 5000);
    
    // Lighter DOM observation
    const observer = new MutationObserver(() => {
      setTimeout(removeEmergentBadge, 500);
    });
    observer.observe(document.body, { 
      childList: true, 
      subtree: false  // Only watch direct children, not deep subtree
    });

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="App bg-slate-900 min-h-screen">
      <Header />
      <Hero />
      <TechnologySection />
      <ProductsSection />
      <MarketsSection />
      <TargetsSection />
      <AssetsSection />
      
      {/* Invest-only sections */}
      {mode === 'invest' && (
        <>
          <RoadmapSection />
          <TeamSection />
          <InvestmentSection />
        </>
      )}
      
      <ROICalculator />
      <ContactSection />
      <Footer />
      <Toaster />
    </div>
  );
}

function KeynoteRoute() {
  useEffect(() => {
    setSeo({
      title: 'MSML Keynote — General Harmonics',
      description: 'An interactive keynote on MSML: making information lighter by understanding its structure.',
      path: '/msml-keynote', type: 'article',
    });
  }, []);
  return <MSMLKeynote />;
}

function AssetsRoute() {
  useEffect(() => {
    let m = document.head.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'robots'); document.head.appendChild(m); }
    m.setAttribute('content', 'noindex, follow');
    return () => { if (m) m.setAttribute('content', 'index, follow'); };
  }, []);
  return <div className="bg-slate-900 min-h-screen"><AssetsHeader /><div className="pt-20"><AssetsSection /></div></div>;
}

function LegacyRoute() {
  useEffect(() => {
    let m = document.head.querySelector('meta[name="robots"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'robots'); document.head.appendChild(m); }
    m.setAttribute('content', 'noindex, follow');
    return () => { if (m) m.setAttribute('content', 'index, follow'); };
  }, []);
  return <AppContent />;
}

// Old-site section anchors that now live on the Deep Dive perspective (not on the new Overview).
// Legacy shared links like /#products are redirected to /deep-dive#products so they keep working.
// #technology and #hero exist on BOTH perspectives, so they are intentionally excluded.
const LEGACY_DEEP_ANCHORS = new Set([
  'products', 'markets', 'targets', 'assets', 'contact', 'roadmap', 'team',
  'investment', 'about', 'media', 'robotics', 'telecom', 'ai-llms',
]);

function ScrollManager() {
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const onOverview = pathname === '/' || pathname === '/overview';
    const anchor = hash ? decodeURIComponent(hash.slice(1)) : '';
    // Legacy anchor redirect: keep old /#section links working by sending them to Deep Dive.
    if (onOverview && LEGACY_DEEP_ANCHORS.has(anchor)) {
      navigate(`/deep-dive#${anchor}`, { replace: true });
      return;
    }
    if (hash) {
      // Return to a specific section (e.g. /#ip). Poll because target pages are lazy-loaded.
      let tries = 0;
      const tryScroll = () => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'auto', block: 'start' });
          // Re-scroll after lazy content settles so the section lands precisely.
          setTimeout(() => { const e2 = document.querySelector(hash); if (e2) e2.scrollIntoView({ behavior: 'auto', block: 'start' }); }, 500);
        } else if (tries++ < 50) { setTimeout(tryScroll, 120); }
      };
      setTimeout(tryScroll, 60);
    } else {
      // Reset scroll on every route change so fixed headers never overlap carried-over content.
      window.scrollTo(0, 0);
    }
  }, [pathname, hash, navigate]);
  return null;
}

function App() {
  return (
    <ModeProvider>
      <BrowserRouter>
        <ScrollManager />
        <Suspense fallback={<div style={{ minHeight: '100vh', background: '#FFFFFC' }} />}>
          <Routes>
            <Route path="/" element={<GHHomePage />} />
            <Route path="/overview" element={<GHHomePage />} />
            <Route path="/deep-dive" element={<TechnologyPerspective />} />
            <Route path="/technology" element={<Navigate to="/deep-dive" replace />} />
            <Route path="/applications/:slug" element={<DeepDivePage />} />
            <Route path="/strategic" element={<StrategicPage />} />
            <Route path="/msml-keynote" element={<KeynoteRoute />} />
            <Route path="/assets" element={<AssetsRoute />} />
            <Route path="/legacy" element={<LegacyRoute />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ModeProvider>
  );
}

export default App;
